import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile } from "../../../../core/redux/action/userProfile";

import PersonalInformation1 from "./Forms/PersonalInformation1";
import PersonalInformation2 from "./Forms/PersonalInformation2";
import EducationBackground from "./Forms/EducationBackground";
import WorkingExperience from "./Forms/WorkingExperience";

import EditSuccess from "./EditSuccess";

import validationSchema from "./FormModel/validationSchema";
import postDocFormModel from "./FormModel/postDocFormModel";
import formInitialValues from "./FormModel/formInitialValues";

import useStyles from "./styles";
import { getProfile } from "../../../../core/service/users.service";

const steps = [
  "Personal Information",
  "Postdoctoral Information",
  "Education Background",
  "Working Experience",
];
const { formId, formField } = postDocFormModel;

function _renderStepContent(step, data) {
  switch (step) {
    case 0:
      return <PersonalInformation1 formField={formField} userData={data}/>;
    case 1:
      return <PersonalInformation2 formField={formField} />;
    case 2:
      return <EducationBackground formField={formField} />;
    case 3:
      return <WorkingExperience formField={formField} userData={data}/>;
    default:
      return <>Not Found</>;
  }
}

export default function EditPostDocPage() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const currentValidationSchema = validationSchema[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const { role, user: currentUser } = useSelector((state) => state.authReducer);
  const { id } = currentUser;
  const [userData, setUserData] = useState(null);

  function _sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function _submitForm(values, actions) {
    var formData = new FormData();

    for (const [key, value] of Object.entries(values)) {
      if (value?.file) {
        formData.append(key, value.file);
      } else {
        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value ? value : "");
        }
      }
    }
    //post_doc
    formData.append("role", 1);
    formData.append("user_id", id);

    dispatch(updateProfile(formData));

    await _sleep(1000);
    actions.setSubmitting(false);
    setActiveStep(activeStep + 1);
  }

  function _handleSubmit(values, actions) {
    if (isLastStep) {
      _submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  }

  function _handleBack() {
    setActiveStep(activeStep - 1);
  }

  const getUserProfile = async (id) => {
    await getProfile(id)
      .then((res) => {
        const formValues =  mapValues(res);
        //console.log('response ===>', res)
        setUserData(formValues);
      })
      .catch((err) => console.error(err));
  };

  const mapValues = (data) => {
    const formValues = formInitialValues;
    formValues.photo = data?.personal_infomation?.photo;
    formValues.email = data?.email;
    // formValues.password = data?.password;
    formValues.titleNameEng = data?.personal_infomation?.title_name_en;
    formValues.firstNameEng = data?.personal_infomation?.name_en;
    formValues.middleNameEng = data?.personal_infomation?.middle_name_en;
    formValues.lastNameEng = data?.personal_infomation?.surname_en;
    formValues.titleNameThai = data?.personal_infomation?.title_name_th;
    formValues.firstNameThai = data?.personal_infomation?.name_th;
    formValues.middleNameThai = data?.personal_infomation?.middle_name_th;
    formValues.lastNameThai = data?.personal_infomation?.surname_th;
    formValues.gender = data?.personal_infomation?.gender;
    formValues.dateOfBirth = data?.personal_infomation?.birth_date;
    formValues.maritalStatus = data?.personal_infomation?.marital_status;
    formValues.bloodType = data?.personal_infomation?.blood_type;
    formValues.idCard = data?.personal_infomation?.card_id_or_passport;
    formValues.nationality = data?.personal_infomation?.nationality;
    formValues.yearOfTheContract = data?.personal_infomation?.year_contract;

    formValues.faculty = data?.postdoctoral_information?.faculty;
    formValues.department = data?.postdoctoral_information?.department;
    formValues.mobileThai = data?.postdoctoral_information?.mobile_number_th;
    formValues.mobileCountry =
      data?.postdoctoral_information?.mobile_number_country;

    formValues.studentId = data?.personal_infomation?.student_id;
    formValues.mentor_id = data?.my_mentor?.mentor_id;
    formValues.mentorName =
      (data?.my_mentor?.title_name_en ?? "") +
      (data?.my_mentor?.middle_name_en
        ? " " + data?.my_mentor?.middle_name_en
        : "") +
      (data?.my_mentor?.name_en ? " " + data?.my_mentor?.name_en : "");
    formValues.mentorEmail = data?.my_mentor?.mentor_id;

    formValues.startFellowship = data?.postdoctoral_information?.start_reciving;
    formValues.contractEnd = data?.postdoctoral_information?.contract_end;
    formValues.fieldResearch = data?.postdoctoral_information?.field_research;
    formValues.addressInThailand = data?.postdoctoral_information?.address_th;
    formValues.addressInTheCountryOfResidence =
      data?.postdoctoral_information?.address_en;

    formValues.nameOfTheBachelorDegree =
      data?.postdoc_educational_background?.name_bachelor_degree;
    formValues.graduatedUniversityInstituteBachelorDegree =
      data?.postdoc_educational_background?.graduated_university_bachelor_degree;
    formValues.countryBachelorDegree =
      data?.postdoc_educational_background?.country_bachelor_degree;
    formValues.dateOfBachelorDegreeGraduation =
      data?.postdoc_educational_background?.date_bachelor_degree;
    formValues.nameOfMasterDegree =
      data?.postdoc_educational_background?.name_master_degree;
    formValues.graduatedUniversityInstituteMasterDegree =
      data?.postdoc_educational_background?.graduated_university_master_degree;
    formValues.countryMasterDegree =
      data?.postdoc_educational_background?.country_master_degree;
    formValues.dateOfMasterDegreeGraduation =
      data?.postdoc_educational_background?.date_master_degree;
    formValues.doctoralDegreeBackground =
      data?.postdoc_educational_background?.name_doctoral_degree;
    formValues.graduatedUniversityInstituteDoctoral =
      data?.postdoc_educational_background?.graduated_university_doctoral_degree;
    formValues.countryDoctoral =
      data?.postdoc_educational_background?.country_doctoral_degree;
    formValues.dateGraduationDoctoral =
      data?.postdoc_educational_background?.date_doctoral_degree;

    // formValues.publication
    // formValues.awards
    formValues.cvOrResume = data?.personal_infomation?.cv_resume;

    const work_exp_arr = [];

    data?.working_experience?.forEach((element) => {
      work_exp_arr.push({
        position: element.position,
        companyOrganization: element.compony,
        country: element.country,
        started: element.started,
        ended: element.ended,
        stillInRole: element.still_role,
        id: element.id,
      });
    });

    if (work_exp_arr.length > 0) {
      formValues.workingExperienceList = work_exp_arr;
    }
    return formValues;
    //setUserData(formValues);
  };

  useEffect(() => {
    getUserProfile(id);
  }, []);

  return (
    <Container>
      <Box sx={{ textAlign: "center" }}>
        <>
          <Typography component="h1" variant="h4" align="center">
            Edit Profile for C2F Postdoc
          </Typography>
          <Stepper activeStep={activeStep} className={classes.stepper}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {activeStep === steps.length ? (
            <EditSuccess />
          ) : (
            <Formik
              //enableReinitialize={true}
              initialValues={formInitialValues}
              validationSchema={currentValidationSchema}
              onSubmit={_handleSubmit}
            >
              {({ isSubmitting }) => {
                return (
                  <Form id={formId}>
                    {_renderStepContent(activeStep, formInitialValues)}

                    <Box className={classes.buttons}>
                      {activeStep !== 0 && (
                        <Button
                          onClick={_handleBack}
                          className={classes.button}
                        >
                          Back
                        </Button>
                      )}
                      <Box className={classes.wrapper}>
                        <LoadingButton
                          loading={isSubmitting}
                          disabled={isSubmitting}
                          type="submit"
                          variant="contained"
                          color="primary"
                          className={classes.button}
                        >
                          {isLastStep ? "Update" : "Next"}
                        </LoadingButton>
                      </Box>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          )}
        </>
      </Box>
    </Container>
  );
}
